import React, { useEffect, useState, useRef } from "react";
import useMediaQuery from "./useMediaQuery";
import AlertMessage from "./AlertMessage";
import { Say } from "../../languages";

export const CreditCardTokenExField = (props) => {
  const {
    fieldId = "tokenExField",
    renderMessage = "",
    secondInteraction,
    fieldAlertMessagePhraseKey,
    iframeConfig,
    refIframe,
    isValid = false,
    classList = "",
    initTimeout = 6000,
    onInitTimeout = () => {},
    loadDeadline = 45000,
    onLoadDeadline = () => {},
    onLoad = () => {},
    onCardTypeChange = () => {},
    onTokenize,
    onValidate = () => {},
    onExpire = () => {},
    onBlur = () => {},
    onFocus = () => {},
    setTokenExError,
    handleIframeError = () => {},
    handleIframeLoadError = () => {}
  } = props;
  const iframeWrapperRef = useRef(null);
  const { deviceScreen } = useMediaQuery();

  const [visibleFrameClass, setVisibleFrameClass] = useState("invisible-frame");

  let validityClass = "";
  if (isValid && secondInteraction) {
    validityClass = "has-success";
  } else if (!isValid && secondInteraction) {
    validityClass = "has-error";
  }

  useEffect(() => {
    let initTimeoutId = null;
    let loadDeadlineId = null;

    const clearLoadTimers = () => {
      if (initTimeoutId) {
        clearTimeout(initTimeoutId);
        initTimeoutId = null;
      }
      if (loadDeadlineId) {
        clearTimeout(loadDeadlineId);
        loadDeadlineId = null;
      }
    };

    // Re-arms instead of firing while the tab is hidden: background tabs get both timer
    // throttling and deprioritized iframe loading, so a verdict reached there is about an
    // iframe that never had a fair chance.
    const armLoadDeadline = () => {
      loadDeadlineId = setTimeout(() => {
        if (document.hidden) {
          armLoadDeadline();
          return;
        }
        onLoadDeadline();
      }, loadDeadline);
    };

    const removeIframe = () => {
      if (refIframe.current) {
        refIframe.current.remove();
        refIframe.current = null;
      }
    };

    const handleIframeLoad = () => {
      // Read per effect run, not at module scope: the script comes from the host page, and
      // a module-scope capture of undefined can never recover across refreshes.
      const TokenEx = window.TokenEx;

      if (!TokenEx?.Iframe) {
        handleIframeLoadError(
          new Error("TokenEx script unavailable: window.TokenEx is not defined")
        );
        return;
      }

      const inputTypeConfig = ["x-small", "small"].includes(deviceScreen)
        ? { inputType: "tel" }
        : {};

      initTimeoutId = setTimeout(onInitTimeout, initTimeout);
      armLoadDeadline();

      try {
        refIframe.current = new TokenEx.Iframe(fieldId, { ...iframeConfig, ...inputTypeConfig });

        refIframe.current.on("tokenize", onTokenize);
        refIframe.current.on("validate", onValidate);
        refIframe.current.on("expired", onExpire);
        refIframe.current.on("cardTypeChange", onCardTypeChange);
        refIframe.current.on("blur", onBlur);
        refIframe.current.on("focus", onFocus);
        refIframe.current.on("load", () => {
          clearLoadTimers(); // Clear init timeout
          setTimeout(() => setVisibleFrameClass("visible-frame"), 10); // Prevent glitch
          onLoad();
        });
        refIframe.current.on("error", (error) => handleIframeError(error));

        refIframe.current.load();
      } catch (error) {
        clearLoadTimers();
        handleIframeLoadError(error);
      }
    };

    // First, remove any existing iframe
    removeIframe();

    // If we have a valid iframe config, initialize the iframe
    if (iframeConfig?.authenticationKey) {
      setVisibleFrameClass("invisible-frame");
      handleIframeLoad();
    }

    return () => {
      clearLoadTimers();
      removeIframe();
    };
  }, [refIframe, iframeConfig, setTokenExError]);

  return (
    <div
      className={`form-group has-icon ${validityClass} ${visibleFrameClass}`}
      data-field-container={fieldId}
      data-field-is-valid={isValid}>
      <label className="payment-info-heading cc-heading is-required" htmlFor="pan">
        <Say phrase="payment_step.field_labels.card_number" /> <sup>*</sup>
      </label>
      <AlertMessage alertType={"danger"} visible={!!fieldAlertMessagePhraseKey}>
        <Say phrase={fieldAlertMessagePhraseKey} />
      </AlertMessage>
      <div className="form-control-wrapper iframe-wrapper">
        <div
          id={fieldId}
          ref={iframeWrapperRef}
          data-phrase-key="payment_step.field_labels.card_number"
          className={`form-control form-input-iframe ${classList}`}
        />
        {renderMessage(props)}
      </div>
    </div>
  );
};

export default CreditCardTokenExField;
